import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, TrendingUp, EyeOff, Bell } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getCourseList, getCourse, CourseMetadata } from '../config/courses';
import { getStreak } from '../lib/supabase';
import { getStreakMessage } from '../lib/scoring';
import { supabase } from '../config/supabase';
import {
  getCourseProgress,
  getNextRecommendation,
  formatTimeAgo,
  formatActivityName,
  formatTestCountdown,
  Recommendation,
  CourseProgress,
} from '../lib/recommendations';
import { getAllMessages, getReadMessageIds } from '../lib/supabase';
import { DashboardRecommendationCard } from './RecommendationCard';

interface ActiveCourseData {
  courseId: string;
  courseTitle: string;
  courseIcon: string;
  testDate?: string;
  progress: CourseProgress;
  recommendation: Recommendation;
}

export function Dashboard() {
  const { user } = useAuth();
  const [streak, setStreak] = useState(0);
  const isAdmin = user?.role === 'admin';
  // Admins see all courses (including hidden), students only see visible ones
  const [courses] = useState<CourseMetadata[]>(() => getCourseList(isAdmin));
  const [activeCourses, setActiveCourses] = useState<ActiveCourseData[]>([]);
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    async function loadData() {
      if (!user) return;

      // Update last_active on app load
      supabase.from('users').update({
        last_active: new Date().toISOString(),
      }).eq('id', user.id).then(() => {
        console.log('last_active updated');
      });

      const streakCount = await getStreak(user.id);
      setStreak(streakCount);

      // Load unread message count
      const [allMsgs, readIds] = await Promise.all([
        getAllMessages(),
        getReadMessageIds(user.id),
      ]);
      setUnreadCount(allMsgs.filter(m => !readIds.includes(m.id)).length);

      // Load recommendations for active courses
      setIsLoadingRecommendations(true);
      const activeCoursesData: ActiveCourseData[] = [];

      for (const courseCard of courses) {
        const course = await getCourse(courseCard.id);
        if (!course) continue;

        const progress = await getCourseProgress(user.id, courseCard.id, course);

        // Only show if user has started this course
        if (progress.hasStarted) {
          const recommendation = getNextRecommendation(courseCard.id, progress, course);

          activeCoursesData.push({
            courseId: courseCard.id,
            courseTitle: courseCard.title,
            courseIcon: courseCard.icon,
            testDate: course.testDate,
            progress,
            recommendation,
          });
        }
      }

      setActiveCourses(activeCoursesData);
      setIsLoadingRecommendations(false);
    }

    loadData();
  }, [user, courses]);

  const streakInfo = getStreakMessage(streak);

  return (
    <div className="dashboard">
      <div className="dashboard-greeting" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1>Hallo {user?.name?.split(' ')[0]}! 👋</h1>
        <Link
          to="/messages"
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            background: 'rgba(255,255,255,0.08)',
            borderRadius: '12px',
            textDecoration: 'none',
          }}
        >
          <Bell size={20} style={{ color: '#9fa8da' }} />
          {unreadCount > 0 && (
            <span style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              background: '#ef5350',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 700,
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {unreadCount}
            </span>
          )}
        </Link>
      </div>

      <div className="streak-card">
        <div className="streak-icon">
          <Flame size={28} />
        </div>
        <div className="streak-info">
          <span className="streak-emoji">{streakInfo.emoji}</span>
          <span className="streak-text">{streakInfo.message}</span>
        </div>
      </div>

      {/* Active Course Recommendations */}
      {!isLoadingRecommendations && activeCourses.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h2 className="section-title" style={{ marginBottom: '12px' }}>
            Weiter lernen
          </h2>
          {activeCourses.map(ac => {
            const testCountdown = ac.testDate
              ? formatTestCountdown(ac.testDate)
              : undefined;

            const lastAct = ac.progress.lastActivity ? {
              name: formatActivityName(ac.progress.lastActivity.action),
              timeAgo: formatTimeAgo(ac.progress.lastActivity.created_at),
              score: (ac.progress.lastActivity.details as { percentage?: number })?.percentage,
            } : undefined;

            return (
              <DashboardRecommendationCard
                key={ac.courseId}
                courseId={ac.courseId}
                courseTitle={ac.courseTitle}
                courseIcon={ac.courseIcon}
                testCountdown={testCountdown}
                lastActivity={lastAct}
                recommendation={ac.recommendation}
              />
            );
          })}
        </div>
      )}

      <h2 className="section-title">Deine Kurse</h2>

      <div className="course-grid">
        {courses.map((course) => (
          <Link
            key={course.id}
            to={`/course/${course.id}`}
            className="course-card"
            style={{
              opacity: course.hidden ? 0.6 : 1,
              position: 'relative',
            }}
          >
            <div className="course-icon">{course.icon}</div>
            <div className="course-info">
              <h3>{course.title}</h3>
              <span className="course-subject">{course.subject}</span>
            </div>
            <div className="course-progress">
              <TrendingUp size={18} />
            </div>
            {/* Hidden badge for admins */}
            {isAdmin && course.hidden && (
              <div style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: 'rgba(255, 152, 0, 0.2)',
                border: '1px solid rgba(255, 152, 0, 0.4)',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '11px',
                color: '#ff9800',
              }}>
                <EyeOff size={12} />
                Ausgeblendet
              </div>
            )}
          </Link>
        ))}
      </div>

      {courses.length === 0 && (
        <div className="empty-state">
          <p>Noch keine Kurse verfügbar.</p>
        </div>
      )}
    </div>
  );
}
